<script setup lang="ts">
/**
 * Stained-glass wallpaper-group thumbnail.
 *
 * Each tile is filled with a colour theme — a background + a few coloured
 * polygon regions. Every wallpaper-group transform is applied, so the tile
 * repeats into a stained-glass window. Cell outlines are drawn on top.
 *
 * Pass `theme` explicitly (e.g. from WPG_THEMES or a random theme) or omit
 * it to fall back to a stable random theme derived from the wpgId.
 */
import { apply, transformToMatrix, type Mat } from '@/geometry/affine';
import {
  buildFundamentalVariant,
  randomTheme,
  WPG_THEMES,
  type ArtistTile,
  type LatticeParams,
  type WallpaperGroupId,
  type WpgTheme,
} from '@/patterns/wallpaper';
import { computed, getCurrentInstance } from 'vue';

const props = withDefaults(
  defineProps<{
    wpgId: WallpaperGroupId;
    latticeParams?: LatticeParams;
    /** how the drawing tile is presented — matches the preset/favourite it previews */
    artistTile?: ArtistTile;
    theme?: WpgTheme;
    size?: number;
    /**
     * When true, the SVG is positioned absolutely to fill its parent container
     * (parent must be position:relative + overflow:hidden). Uses slice scaling so
     * tiles cover the full area. Ignores `size`.
     */
    fill?: boolean;
  }>(),
  { size: 56, fill: false, artistTile: 'standard' },
);

/** Unique per-instance prefix so clip-path ids don't collide between thumbnails. */
const uid = getCurrentInstance()?.uid ?? 0;
const clipId = (i: number): string => `wpg-clip-${uid}-${i}`;

type Built = { polygon: [number, number][]; cellMatrices: Mat[]; firstRingMatrices: Mat[] };

const built = computed<Built | null>(() => {
  try {
    const { polygon, variant } = buildFundamentalVariant(
      props.wpgId,
      props.latticeParams ?? {},
      props.artistTile,
    );
    // Cell placements collapse the op-invariant stabilizer copies (the hexagon
    // is fixed by its own rotations), so each cell — and its themed motif — is
    // drawn exactly once instead of stacking N deep.
    const cellGen = variant.cellTransformsInRange ?? variant.transformsInRange;
    const cellMatrices = cellGen.call(variant, 7).map(transformToMatrix);
    const firstRingMatrices = cellGen.call(variant, 1).map(transformToMatrix);
    return { polygon, cellMatrices, firstRingMatrices };
  } catch {
    return null;
  }
});

/** Stable per-wpgId fallback theme — same hue every render for a given group. */
const WPG_SEED: Record<string, number> = {
  p1: 17, p2: 43, pm: 89, pg: 137, pmm: 181, pmg: 229, pgg: 271,
  cm: 317, cmm: 359, p4: 37, p4m: 97, p4g: 157, p3: 211, p3m1: 251, p31m: 293,
  p6: 331, p6m: 7,
};

const resolvedTheme = computed<WpgTheme>(() => {
  // First check if there's a curated theme for this WPG
  if (props.theme) return props.theme;
  const curatedTheme = WPG_THEMES[props.wpgId];
  if (curatedTheme) return curatedTheme;
  // Fall back to stable random theme
  const seed = WPG_SEED[props.wpgId] ?? 42;
  return randomTheme((() => {
    let s = seed * 6364136223846793005 + 1442695040888963407;
    return () => {
      s = (s * 6364136223846793005 + 1442695040888963407) | 0;
      return ((s >>> 0) & 0xffff) / 0x10000;
    };
  })());
});

const view = computed(() => {
  const b = built.value;
  if (!b) return { x: 0, y: 0, w: 1, h: 1 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const m of b.firstRingMatrices) {
    for (const p of b.polygon) {
      const [x, y] = apply(m, p);
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  const pad = 0.06 * Math.max(maxX - minX, maxY - minY);
  return { x: minX - pad, y: minY - pad, w: maxX - minX + 2 * pad, h: maxY - minY + 2 * pad };
});

function pts(points: [number, number][], m: Mat): string {
  return points.map((p) => {
    const [x, y] = apply(m, p);
    return `${x.toFixed(4)},${y.toFixed(4)}`;
  }).join(' ');
}

function cellPath(polygon: [number, number][], m: Mat): string {
  return polygon.map((p, i) => {
    const [x, y] = apply(m, p);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(4)} ${y.toFixed(4)}`;
  }).join(' ') + ' Z';
}

const cells = computed(() => {
  const b = built.value;
  const t = resolvedTheme.value;
  if (!b) return [];
  return b.cellMatrices.map((m) => ({
    bg: cellPath(b.polygon, m),
    regions: t.regions.map((r) => ({
      pts: pts(r.pts, m),
      fill: t.colors[r.ci],
    })),
    outline: cellPath(b.polygon, m),
  }));
});
</script>

<template>
  <svg
    class="wpg-thumb"
    :class="{ 'wpg-thumb--fill': fill }"
    :width="fill ? undefined : size"
    :height="fill ? undefined : size"
    :viewBox="`${view.x} ${view.y} ${view.w} ${view.h}`"
    :preserveAspectRatio="fill ? 'xMidYMid slice' : 'xMidYMid meet'"
    aria-hidden="true"
  >
    <g v-if="built">
      <!-- Per-cell clip paths so the themed regions never bleed past the cell -->
      <defs>
        <clipPath v-for="(c, i) in cells" :id="clipId(i)" :key="`clip-${i}`">
          <path :d="c.bg" />
        </clipPath>
      </defs>
      <!-- Background fills -->
      <path
        v-for="(c, i) in cells"
        :key="`bg-${i}`"
        :d="c.bg"
        :fill="resolvedTheme.bg"
      />
      <!-- Coloured regions, clipped to their cell -->
      <g v-for="(c, i) in cells" :key="`regions-${i}`" :clip-path="`url(#${clipId(i)})`">
        <polygon
          v-for="(r, j) in c.regions"
          :key="j"
          :points="r.pts"
          :fill="r.fill"
        />
      </g>
      <!-- Cell outlines on top -->
      <path
        v-for="(c, i) in cells"
        :key="`outline-${i}`"
        :d="c.outline"
        class="thumb-outline"
        vector-effect="non-scaling-stroke"
      />
    </g>
  </svg>
</template>

<style scoped>
.wpg-thumb {
  display: block;
}

.wpg-thumb--fill {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: red !important; /* fallback for missing theme */
}

.thumb-outline {
  fill: none;
  stroke: rgba(0, 0, 0, 0.35);
  stroke-width: 0.8;
}
</style>
